import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { getAsset } from '../../services/assetService';
import { getFlocs } from '../../services/flocService';
import { commissionAsset } from '../../services/assetInstallationService';
import { Asset, FunctionalLocation } from '@easydone/shared';

export function CommissionAssetPage() {
  const { assetId } = useParams<{ assetId: string }>();
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [asset, setAsset] = useState<Asset | null>(null);
  const [flocs, setFlocs] = useState<FunctionalLocation[]>([]);
  const [selectedFloc, setSelectedFloc] = useState('');
  const [conditionAtInstall, setConditionAtInstall] = useState(100);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [commissioning, setCommissioning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [assetId, currentOrg]);

  const loadData = async () => {
    if (!assetId || !currentOrg) return;

    setLoading(true);
    setError('');

    try {
      const [assetData, flocsData] = await Promise.all([
        getAsset(currentOrg.id, assetId),
        getFlocs(currentOrg.id),
      ]);

      if (!assetData) {
        setError('Asset not found');
        return;
      }

      if (assetData.lifecycleStatus !== 'in_storage') {
        setError('Asset must be in storage to commission');
        return;
      }

      setAsset(assetData);
      setFlocs(flocsData.filter(f => !f.installedAssetId)); // Only vacant FLOCs
      setConditionAtInstall(assetData.conditionScore);
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCommission = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrg || !currentUser || !assetId) return;

    if (!selectedFloc) {
      setError('Please select a functional location');
      return;
    }

    setCommissioning(true);
    setError('');

    try {
      await commissionAsset(
        currentOrg.id,
        {
          assetId,
          flocId: selectedFloc,
          conditionAtInstall,
          installationNotes: notes,
        },
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown'
      );

      navigate(`/assets/${assetId}`);
    } catch (err: any) {
      console.error('Failed to commission asset:', err);
      setError(err.message || 'Failed to commission asset');
    } finally {
      setCommissioning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">{error || 'Asset not found'}</p>
          <button onClick={() => navigate('/assets')} className="btn btn-secondary mt-4">
            Back to Assets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Commission Asset</h1>
        <p className="text-gray-600 mt-1">
          Install asset into a functional location
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Asset Info */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold mb-3">Asset Information</h2>
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-gray-600">Name:</span>
            <span className="ml-2 font-medium">{asset.name}</span>
          </div>
          <div>
            <span className="text-gray-600">Serial Number:</span>
            <span className="ml-2 font-medium">{asset.serialNumber}</span>
          </div>
          <div>
            <span className="text-gray-600">Type:</span>
            <span className="ml-2 font-medium">{asset.type}</span>
          </div>
          <div>
            <span className="text-gray-600">Current Location:</span>
            <span className="ml-2 font-medium">{asset.storageLocation}</span>
          </div>
          <div>
            <span className="text-gray-600">Current Condition:</span>
            <span className={`ml-2 font-medium ${
              asset.conditionScore >= 80 ? 'text-green-600' :
              asset.conditionScore >= 60 ? 'text-yellow-600' :
              'text-red-600'
            }`}>
              {asset.conditionScore} (Grade {asset.conditionGrade})
            </span>
          </div>
        </div>
      </div>

      {/* Commission Form */}
      <form onSubmit={handleCommission} className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target Functional Location *
          </label>
          {flocs.length === 0 ? (
            <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
              No vacant functional locations available. All FLOCs have assets installed.
            </div>
          ) : (
            <select
              value={selectedFloc}
              onChange={(e) => setSelectedFloc(e.target.value)}
              className="input w-full"
              required
            >
              <option value="">Select a functional location...</option>
              {flocs.map((floc) => (
                <option key={floc.id} value={floc.id}>
                  {floc.name} ({floc.code}) - {floc.criticality.toUpperCase()}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Condition at Installation *
          </label>
          <input
            type="number"
            value={conditionAtInstall}
            onChange={(e) => setConditionAtInstall(Number(e.target.value))}
            min="0"
            max="100"
            className="input w-full"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Assess condition before installation (0-100)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Installation Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes about the installation..."
            className="input w-full"
            rows={3}
          />
        </div>

        <div className="flex justify-between pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate(`/assets/${assetId}`)}
            className="btn btn-secondary"
            disabled={commissioning}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={commissioning || flocs.length === 0}
          >
            {commissioning ? 'Commissioning...' : 'Commission Asset'}
          </button>
        </div>
      </form>
    </div>
  );
}
